'use client'

import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { createImageExpandButtonElement } from '@/components/ImageExpandButton'
import {
  collectZoomableImages,
  dedupeLightboxItems,
  findLightboxIndex,
  isDirectZoomableImageClick,
  isImageFileHref,
  isZoomableArticleImage,
  resolveLightboxSrc,
  type LightboxItem,
} from '@/lib/lightbox-image'
import { openFancybox } from '@/lib/open-fancybox'

const EMPTY_ITEMS: LightboxItem[] = []
const CLICK_THROUGH_MS = 500

function stopImageNavigation(event: Event) {
  event.preventDefault()
  event.stopPropagation()
  if ('stopImmediatePropagation' in event) {
    event.stopImmediatePropagation()
  }
}

function expandHostForImage(img: HTMLImageElement): HTMLElement {
  const figure = img.closest('figure')
  if (figure instanceof HTMLElement) return figure

  const existing = img.closest('.haber-image-expand-host')
  if (existing instanceof HTMLElement) return existing

  const picture = img.closest('picture')
  const anchor = img.closest('a')
  const toWrap =
    anchor instanceof HTMLElement && picture && anchor.contains(picture)
      ? anchor
      : picture instanceof HTMLElement
        ? picture
        : anchor instanceof HTMLElement
          ? anchor
          : img

  const wrap = document.createElement('span')
  wrap.className = 'haber-image-expand-host'
  toWrap.parentNode?.insertBefore(wrap, toWrap)
  wrap.appendChild(toWrap)
  return wrap
}

export function useArticleImageLightbox(
  contentRef: RefObject<HTMLElement | null>,
  heroSrc: string | null,
  heroAlt = '',
  extraItems: LightboxItem[] = EMPTY_ITEMS,
  heroOriginEl?: RefObject<HTMLElement | null>,
) {
  const readyRef = useRef(false)

  useEffect(() => {
    readyRef.current = false
    const timer = window.setTimeout(() => {
      readyRef.current = true
    }, CLICK_THROUGH_MS)
    return () => window.clearTimeout(timer)
  }, [heroSrc])

  const buildItems = useCallback(() => {
    const contentItems = contentRef.current ? collectZoomableImages(contentRef.current) : []
    const heroItem = heroSrc ? [{ src: heroSrc, alt: heroAlt, title: heroAlt }] : []
    return dedupeLightboxItems([...heroItem, ...contentItems, ...extraItems])
  }, [contentRef, extraItems, heroAlt, heroSrc])

  const openAt = useCallback(
    (src?: string, originEl?: Element | null) => {
      if (!readyRef.current) return
      const items = buildItems()
      if (items.length === 0) return
      openFancybox(items, findLightboxIndex(items, src), originEl)
    },
    [buildItems],
  )

  useEffect(() => {
    const root = contentRef.current
    if (!root) return

    const mountExpandButtons = () => {
      root.querySelectorAll('img').forEach((img) => {
        if (!isZoomableArticleImage(img)) return
        const host = expandHostForImage(img)
        if (host.querySelector(':scope > .haber-image-expand')) return
        const btn = createImageExpandButtonElement(
          img.alt?.trim() ? `${img.alt} görselini büyüt` : 'Görseli büyüt',
        )
        btn.addEventListener('click', (event) => {
          stopImageNavigation(event)
          openAt(resolveLightboxSrc(img), img)
        })
        host.appendChild(btn)
      })
    }

    root.querySelectorAll('img').forEach((img) => {
      if (isZoomableArticleImage(img)) img.draggable = false
    })

    const onClick = (event: MouseEvent) => {
      if (!event.isTrusted || event.button !== 0) return
      const target = event.target
      if (!(target instanceof Element)) return
      if (target.closest('.haber-image-expand')) return

      const img = isDirectZoomableImageClick(target, root)
      const anchor = target.closest('a')
      const imageLink =
        !img &&
        anchor instanceof HTMLAnchorElement &&
        isImageFileHref(anchor.getAttribute('href')) &&
        (target === anchor || anchor.contains(target))

      if (!img && !imageLink) return
      stopImageNavigation(event)
    }

    const onDragStart = (event: DragEvent) => {
      const img = isDirectZoomableImageClick(event.target, root)
      const anchor = event.target instanceof Element ? event.target.closest('a') : null
      if (img || (anchor && isImageFileHref(anchor.getAttribute('href')))) {
        stopImageNavigation(event)
      }
    }

    mountExpandButtons()
    const observer = new MutationObserver(mountExpandButtons)
    observer.observe(root, { childList: true, subtree: true })
    root.addEventListener('click', onClick, true)
    root.addEventListener('dragstart', onDragStart, true)
    return () => {
      observer.disconnect()
      root.removeEventListener('click', onClick, true)
      root.removeEventListener('dragstart', onDragStart, true)
    }
  }, [contentRef, openAt])

  return {
    openHero: (event?: { isTrusted?: boolean }) => {
      if (event && event.isTrusted === false) return
      openAt(heroSrc || undefined, heroOriginEl?.current)
    },
  }
}
